import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Link2, Linkedin, Twitter } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { API } from "@/lib/api";

export const ArticleActions = ({ post }) => {
  const [likes, setLikes] = useState(post.likes || 0);
  const [dislikes, setDislikes] = useState(post.dislikes || 0);
  const [voted, setVoted] = useState(null);

  useEffect(() => {
    setVoted(localStorage.getItem(`vote-${post.id}`));
  }, [post.id]);

  const url = `${window.location.origin}/insights#post-${post.id}`;
  const text = `${post.title} — Globus Financial Services`;

  const vote = async (reaction) => {
    if (voted) {
      toast.info("You've already reacted to this article");
      return;
    }
    try {
      const res = await axios.post(`${API}/posts/${post.id}/react`, { reaction });
      setLikes(res.data.likes ?? 0);
      setDislikes(res.data.dislikes ?? 0);
      localStorage.setItem(`vote-${post.id}`, reaction);
      setVoted(reaction);
    } catch {
      toast.error("Could not record your reaction");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Article link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const voteBtn = (reaction) =>
    `flex items-center gap-2 text-xs border rounded-sm px-3.5 py-2 transition-colors ${
      voted === reaction
        ? "border-gold text-gold bg-gold/10"
        : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/25"
    }`;

  const shareBtn =
    "h-8 w-8 flex items-center justify-center border border-white/10 rounded-sm text-muted-foreground hover:text-gold hover:border-gold/40 transition-colors";

  return (
    <div className="mt-8 pt-6 border-t border-white/10">
      <div className="flex flex-wrap items-center gap-3" data-testid={`article-actions-${post.id}`}>
        <button data-testid={`like-${post.id}`} onClick={() => vote("like")} className={voteBtn("like")} aria-label="Like article">
          <ThumbsUp size={14} /> {likes}
        </button>
        <button data-testid={`dislike-${post.id}`} onClick={() => vote("dislike")} className={voteBtn("dislike")} aria-label="Dislike article">
          <ThumbsDown size={14} /> {dislikes}
        </button>
        <span className="w-px h-5 bg-white/10 mx-1" aria-hidden="true" />
        <a
          data-testid={`share-whatsapp-${post.id}`}
          href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={shareBtn}
          aria-label="Share on WhatsApp"
        >
          <WhatsAppIcon size={15} />
        </a>
        <a
          data-testid={`share-linkedin-${post.id}`}
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={shareBtn}
          aria-label="Share on LinkedIn"
        >
          <Linkedin size={15} />
        </a>
        <a
          data-testid={`share-x-${post.id}`}
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={shareBtn}
          aria-label="Share on X"
        >
          <Twitter size={15} />
        </a>
        <button data-testid={`share-copy-${post.id}`} onClick={copy} className={shareBtn} aria-label="Copy link">
          <Link2 size={15} />
        </button>
      </div>
      <p className="mt-5 text-[11px] text-muted-foreground leading-relaxed" data-testid={`disclaimer-${post.id}`}>
        Disclaimer: For general information only — not investment, legal or financial advice. Data points, if any, are
        as reported by the cited sources. Loan terms depend on lender policies; please verify independently before acting.
      </p>
    </div>
  );
};
